// The tiny "R" button that lives on top of Serato. Dark, round, ~30px,
// with a red pending-count badge in the top-right corner.
//
// Drag behavior: implemented in JS (not via -webkit-app-region) because
// at 44×44 the OS-drag-eligible ring around the button is too thin to
// grab reliably. We hook mousedown on the button, watch global
// mousemove for any pixel of movement, and:
//   - if the cursor never moves before mouseup → treat as a click and
//     expand the panel
//   - if the cursor moves → forward dx,dy to the Electron main process
//     so it can reposition the window
// This means the whole window is grabbable everywhere AND the button
// still acts as a button. See electron/main.js#drag-window handler.

import React, { useRef } from "react";
import BrandLogo from "../components/BrandLogo";
import { t } from "../i18n";

interface Props {
  count: number;
  isOnline: boolean;
  // Changes whenever a new request arrives. Used as React `key` on the
  // badge so the pulse CSS animation replays cleanly.
  pulseTrigger: number;
  onExpand: () => void;
}

const DRAG_THRESHOLD_PX = 3;

export const FloatingButton: React.FC<Props> = ({
  count,
  isOnline,
  pulseTrigger,
  onExpand,
}) => {
  // Tracks the in-flight gesture between mousedown and mouseup. null when
  // not pressing. We only care about totals on mouseup, but we update
  // startScreen* on every mousemove so the next dx,dy is relative to
  // wherever the window just moved to (otherwise the IPC adds up
  // cumulatively and the window flies off-screen).
  const dragRef = useRef<{
    startScreenX: number;
    startScreenY: number;
    moved: boolean;
  } | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Left button only — secondary clicks shouldn't initiate drag.
    if (e.button !== 0) return;

    dragRef.current = {
      startScreenX: e.screenX,
      startScreenY: e.screenY,
      moved: false,
    };

    const onMove = (ev: MouseEvent) => {
      const state = dragRef.current;
      if (!state) return;
      const dx = ev.screenX - state.startScreenX;
      const dy = ev.screenY - state.startScreenY;
      if (
        !state.moved &&
        (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)
      ) {
        state.moved = true;
      }
      if (state.moved) {
        window.electronAPI?.dragWindowBy(dx, dy);
        // Reset baseline so the next delta is relative to the current
        // window position — otherwise the dx/dy keeps growing.
        state.startScreenX = ev.screenX;
        state.startScreenY = ev.screenY;
      }
    };

    const onUp = () => {
      const wasDragged = dragRef.current?.moved ?? false;
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (!wasDragged) {
        onExpand();
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div className="desktop-floating-root">
      <button
        type="button"
        className={`floating-button ${!isOnline ? "is-offline" : ""}`}
        onMouseDown={handleMouseDown}
        aria-label={count ? t("floatingButton.openRequestAppPending", { count }) : t("floatingButton.openRequestApp")}
        title={
          isOnline
            ? count
              ? `${count} pending request${count === 1 ? "" : "s"}`
              : "Request App"
            : "Reconnecting…"
        }
      >
        <BrandLogo
          variant="light"
          alt=""
          className="floating-r-img"
        />
      </button>

      {count > 0 && (
        <span
          key={pulseTrigger}
          className="floating-badge"
          aria-label={`${count} pending requests`}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </div>
  );
};
